import { get } from 'lodash';
import React, { FC } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import { useApplication } from 'src/context/ApplicationContext';
import { ALS_ERLEDIGT_MARKIEREN_LABEL, FORM_FIELDS_TYPES, LOEWE_IMAGE_UPLOAD_FIELD_PROPS } from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { ForceTrue } from 'src/lib/validation/ValidationTypes';
import { InhaltTrenner } from 'src/pages/private/online-antrag/antrag-components/Wrapper';
import { getFormStrucWithoutComponentTable, mapindexbyPath } from 'src/utils/formStruc';
import { addIndexToPath, checkReadOnlyInUeberarbeitung } from 'src/utils/helper';
import PruefAssistenz from '../../../../../antrag-components/PruefAssistenz';
import { checkIfPruefAssitenzNeedsToValidate } from '../../../../../application-helper-function/AppHelperfunctions';

// import schemas
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
import { getTextFieldValidationByDatei } from 'src/lib/validation/fieldValidationHelper';
const P = AntragSchema.attributes;
const MAX_CHAR_LENGTH = 5000;

const MarktanalyseAntragLoewe: FC<{ hasComment?: boolean }> = ({ hasComment }) => {
	const { application, setApplicationProperty, readOnly } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);
	return (
		<>
			<PruefAssistenz
				statusPath={P.Projektbeschreibung_Loewe.schema.attributes.Pruefung_Marktanalyse_Pruefstatus.path}
				hinweisPath={P.Projektbeschreibung_Loewe.schema.attributes.Pruefung_Marktanalyse_Hinweis_Fuer_Den_Antragsteller.path}
				ueberarbeitetAmPath={P.Projektbeschreibung_Loewe.schema.attributes.Pruefung_Marktanalyse_Ueberarbeitet_Am.path}
				erledigtPath={P.Projektbeschreibung_Loewe.schema.attributes.Pruefung_Marktanalyse_Erledigt.path}
				formStruc={formStrucPruefAssistenz()}
			/>
			<CreateForm
				formStruc={formStrucWettbewerbstechnologien()}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={updatedReadOnly}
			/>
			<InhaltTrenner marginBottom='2rem' />
			<CreateForm
				formStruc={formStrucAnwendungsgebiete()}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={updatedReadOnly}
			/>
			<InhaltTrenner marginBottom='2rem' />
			<CreateForm
				formStruc={formStrucMarkteintrittsstrategie()}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={updatedReadOnly}
			/>
			<InhaltTrenner marginBottom='2rem' />
			<CreateForm
				formStruc={formStrucMarkteintrittsbarrieren()}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={updatedReadOnly}
			/>
			<InhaltTrenner marginBottom='2rem' />
			<CreateForm
				formStruc={formStrucSchutzrechte()}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={updatedReadOnly}
			/>
			<InhaltTrenner marginBottom='2rem' />
			<CreateForm
				formStruc={formStrucUnternehmensentwicklung()}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={updatedReadOnly}
			/>
		</>
	);
};

export default MarktanalyseAntragLoewe;

const formStrucPruefAssistenz = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Pruefung_Marktanalyse_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: ForceTrue }],
				},
			],
		},
	];
	return struc;
};

const formStrucWettbewerbstechnologien = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{ ...P.Projektbeschreibung_Loewe.schema.attributes.Marktanalyse_Wettbewerbstechnologien, maxCharLength: MAX_CHAR_LENGTH },
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Marktanalyse_Wettbewerbstechnologien_Bilder,
					type: FORM_FIELDS_TYPES.editableTable,
					...LOEWE_IMAGE_UPLOAD_FIELD_PROPS,
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

const formStrucAnwendungsgebiete = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{ ...P.Projektbeschreibung_Loewe.schema.attributes.Anwendungsgebiete_Und_Kundennutzen, maxCharLength: MAX_CHAR_LENGTH },
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Anwendungsgebiete_Und_Kundennutzen_Bilder,
					type: FORM_FIELDS_TYPES.editableTable,
					...LOEWE_IMAGE_UPLOAD_FIELD_PROPS,
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

const formStrucMarkteintrittsstrategie = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Markteintrittsstrategie_Markteinfuehrungserfolge,
					maxCharLength: MAX_CHAR_LENGTH,
					label: 'Markteintrittsstrategie und angedachte Art der wirtschaftlichen Verwertung der Projektergebnisse / Darlegung der Kompetenzen für die geplante Verwertung und den Vertrieb / ggf. beispielhafte Markteinführungserfolge (für Antragsteller und Verbundpartner angeben)',
				},
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Markteintrittsstrategie_Markteinfuehrungserfolge_Bilder,
					type: FORM_FIELDS_TYPES.editableTable,
					...LOEWE_IMAGE_UPLOAD_FIELD_PROPS,
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

const formStrucMarkteintrittsbarrieren = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Moegliche_Markteintrittsbarrieren,
					maxCharLength: MAX_CHAR_LENGTH,
					label: 'Mögliche Markteintrittsbarrieren (Anschlussfinanzierung, Lieferantensicherheit, Produktion, FuE-Bedarf, Vertrieb, etc.)',
				},
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Moegliche_Markteintrittsbarrieren_Bilder,
					type: FORM_FIELDS_TYPES.editableTable,
					...LOEWE_IMAGE_UPLOAD_FIELD_PROPS,
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

const formStrucSchutzrechte = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{ ...P.Projektbeschreibung_Loewe.schema.attributes.Schutzrechte_Lizenzen_Zwischen_Verbundpartnern, maxCharLength: MAX_CHAR_LENGTH },
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Schutzrechte_Lizenzen_Zwischen_Verbundpartnern_Bilder,
					type: FORM_FIELDS_TYPES.editableTable,
					...LOEWE_IMAGE_UPLOAD_FIELD_PROPS,
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

const formStrucUnternehmensentwicklung = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Unternehmensentwicklung_Erwartete_Arbeitsplatzeffekte,
					maxCharLength: MAX_CHAR_LENGTH,
					label: 'Geplante Unternehmensentwicklung nach erfolgreicher Produkt- / Verfahrensentwicklung; erwartete Arbeitsplatzeffekte (für Antragsteller und Verbundpartner angeben)',
				},
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Unternehmensentwicklung_Erwartete_Arbeitsplatzeffekte_Bilder,
					type: FORM_FIELDS_TYPES.editableTable,
					...LOEWE_IMAGE_UPLOAD_FIELD_PROPS,
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

const customFormStrucWettbewerbstechnologien = (application: any, i: number) => {
	const datei = get(
		application,
		addIndexToPath(
			P.Projektbeschreibung_Loewe.schema.attributes.Marktanalyse_Wettbewerbstechnologien_Bilder.schema.attributes.Bild.path,
			i,
			P.Projektbeschreibung_Loewe.schema.attributes.Marktanalyse_Wettbewerbstechnologien_Bilder.path,
		),
	);
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Marktanalyse_Wettbewerbstechnologien_Bilder.schema.attributes.Bildunterschrift_Titel,
					validation: getTextFieldValidationByDatei(datei),
				},
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Marktanalyse_Wettbewerbstechnologien_Bilder.schema.attributes.Copyright_Hinweise,
					validation: getTextFieldValidationByDatei(datei),
				},
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Marktanalyse_Wettbewerbstechnologien_Bilder.schema.attributes.Kurzbeschreibung,
					validation: getTextFieldValidationByDatei(datei),
				},
			],
		},
	];
	return struc;
};

const customFormStrucAnwendungsgebiete = (application: any, i: number) => {
	const datei = get(
		application,
		addIndexToPath(
			P.Projektbeschreibung_Loewe.schema.attributes.Anwendungsgebiete_Und_Kundennutzen_Bilder.schema.attributes.Bild.path,
			i,
			P.Projektbeschreibung_Loewe.schema.attributes.Anwendungsgebiete_Und_Kundennutzen_Bilder.path,
		),
	);
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Anwendungsgebiete_Und_Kundennutzen_Bilder.schema.attributes.Bildunterschrift_Titel,
					validation: getTextFieldValidationByDatei(datei),
				},
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Anwendungsgebiete_Und_Kundennutzen_Bilder.schema.attributes.Copyright_Hinweise,
					validation: getTextFieldValidationByDatei(datei),
				},
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Anwendungsgebiete_Und_Kundennutzen_Bilder.schema.attributes.Kurzbeschreibung,
					validation: getTextFieldValidationByDatei(datei),
				},
			],
		},
	];
	return struc;
};

const customFormStrucMarkteintrittsstrategie = (application: any, i: number) => {
	const datei = get(
		application,
		addIndexToPath(
			P.Projektbeschreibung_Loewe.schema.attributes.Markteintrittsstrategie_Markteinfuehrungserfolge_Bilder.schema.attributes.Bild.path,
			i,
			P.Projektbeschreibung_Loewe.schema.attributes.Markteintrittsstrategie_Markteinfuehrungserfolge_Bilder.path,
		),
	);
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Markteintrittsstrategie_Markteinfuehrungserfolge_Bilder.schema.attributes
						.Bildunterschrift_Titel,
					validation: getTextFieldValidationByDatei(datei),
				},
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Markteintrittsstrategie_Markteinfuehrungserfolge_Bilder.schema.attributes
						.Copyright_Hinweise,
					validation: getTextFieldValidationByDatei(datei),
				},
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Markteintrittsstrategie_Markteinfuehrungserfolge_Bilder.schema.attributes
						.Kurzbeschreibung,
					validation: getTextFieldValidationByDatei(datei),
				},
			],
		},
	];
	return struc;
};

const customFormStrucMarkteintrittsbarrieren = (application: any, i: number) => {
	const datei = get(
		application,
		addIndexToPath(
			P.Projektbeschreibung_Loewe.schema.attributes.Moegliche_Markteintrittsbarrieren_Bilder.schema.attributes.Bild.path,
			i,
			P.Projektbeschreibung_Loewe.schema.attributes.Moegliche_Markteintrittsbarrieren_Bilder.path,
		),
	);
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Moegliche_Markteintrittsbarrieren_Bilder.schema.attributes.Bildunterschrift_Titel,
					validation: getTextFieldValidationByDatei(datei),
				},
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Moegliche_Markteintrittsbarrieren_Bilder.schema.attributes.Copyright_Hinweise,
					validation: getTextFieldValidationByDatei(datei),
				},
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Moegliche_Markteintrittsbarrieren_Bilder.schema.attributes.Kurzbeschreibung,
					validation: getTextFieldValidationByDatei(datei),
				},
			],
		},
	];
	return struc;
};

const customFormStrucSchutzrechte = (application: any, i: number) => {
	const datei = get(
		application,
		addIndexToPath(
			P.Projektbeschreibung_Loewe.schema.attributes.Schutzrechte_Lizenzen_Zwischen_Verbundpartnern_Bilder.schema.attributes.Bild.path,
			i,
			P.Projektbeschreibung_Loewe.schema.attributes.Schutzrechte_Lizenzen_Zwischen_Verbundpartnern_Bilder.path,
		),
	);
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Schutzrechte_Lizenzen_Zwischen_Verbundpartnern_Bilder.schema.attributes
						.Bildunterschrift_Titel,
					validation: getTextFieldValidationByDatei(datei),
				},
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Schutzrechte_Lizenzen_Zwischen_Verbundpartnern_Bilder.schema.attributes
						.Copyright_Hinweise,
					validation: getTextFieldValidationByDatei(datei),
				},
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Schutzrechte_Lizenzen_Zwischen_Verbundpartnern_Bilder.schema.attributes
						.Kurzbeschreibung,
					validation: getTextFieldValidationByDatei(datei),
				},
			],
		},
	];
	return struc;
};

const customFormStrucUnternehmensentwicklung = (application: any, i: number) => {
	const datei = get(
		application,
		addIndexToPath(
			P.Projektbeschreibung_Loewe.schema.attributes.Unternehmensentwicklung_Erwartete_Arbeitsplatzeffekte_Bilder.schema.attributes.Bild.path,
			i,
			P.Projektbeschreibung_Loewe.schema.attributes.Unternehmensentwicklung_Erwartete_Arbeitsplatzeffekte_Bilder.path,
		),
	);
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Unternehmensentwicklung_Erwartete_Arbeitsplatzeffekte_Bilder.schema.attributes
						.Bildunterschrift_Titel,
					validation: getTextFieldValidationByDatei(datei),
				},
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Unternehmensentwicklung_Erwartete_Arbeitsplatzeffekte_Bilder.schema.attributes
						.Copyright_Hinweise,
					validation: getTextFieldValidationByDatei(datei),
				},
				{
					...P.Projektbeschreibung_Loewe.schema.attributes.Unternehmensentwicklung_Erwartete_Arbeitsplatzeffekte_Bilder.schema.attributes
						.Kurzbeschreibung,
					validation: getTextFieldValidationByDatei(datei),
				},
			],
		},
	];
	return struc;
};

export const getFormStrucMarktanalyseAntragLoewe = (application: any) => {
	let result: FormStrucItem[] = [];
	const bilderLengthWettbewerbstechnologien = get(
		application,
		P.Projektbeschreibung_Loewe.schema.attributes.Marktanalyse_Wettbewerbstechnologien_Bilder.path,
		[],
	).length;
	const bilderLengthAnwendungsgebiete = get(
		application,
		P.Projektbeschreibung_Loewe.schema.attributes.Anwendungsgebiete_Und_Kundennutzen_Bilder.path,
		[],
	).length;
	const bilderLengthMarkteintrittsstrategie = get(
		application,
		P.Projektbeschreibung_Loewe.schema.attributes.Markteintrittsstrategie_Markteinfuehrungserfolge_Bilder.path,
		[],
	).length;
	const bilderLengthMarkteintrittsbarrieren = get(
		application,
		P.Projektbeschreibung_Loewe.schema.attributes.Moegliche_Markteintrittsbarrieren_Bilder.path,
		[],
	).length;
	const bilderLengthSchutzrechte = get(
		application,
		P.Projektbeschreibung_Loewe.schema.attributes.Schutzrechte_Lizenzen_Zwischen_Verbundpartnern_Bilder.path,
		[],
	).length;
	const bilderLengthUnternehmensentwicklung = get(
		application,
		P.Projektbeschreibung_Loewe.schema.attributes.Unternehmensentwicklung_Erwartete_Arbeitsplatzeffekte_Bilder.path,
		[],
	).length;

	for (let i = 0; i < bilderLengthWettbewerbstechnologien; i++) {
		const customFormStrucWithIndex = mapindexbyPath(
			customFormStrucWettbewerbstechnologien(application, i),
			i,
			P.Projektbeschreibung_Loewe.schema.attributes.Marktanalyse_Wettbewerbstechnologien_Bilder,
		);
		result.push(...customFormStrucWithIndex);
	}

	for (let i = 0; i < bilderLengthAnwendungsgebiete; i++) {
		const customFormStrucWithIndex = mapindexbyPath(
			customFormStrucAnwendungsgebiete(application, i),
			i,
			P.Projektbeschreibung_Loewe.schema.attributes.Anwendungsgebiete_Und_Kundennutzen_Bilder,
		);
		result.push(...customFormStrucWithIndex);
	}

	for (let i = 0; i < bilderLengthMarkteintrittsstrategie; i++) {
		const customFormStrucWithIndex = mapindexbyPath(
			customFormStrucMarkteintrittsstrategie(application, i),
			i,
			P.Projektbeschreibung_Loewe.schema.attributes.Markteintrittsstrategie_Markteinfuehrungserfolge_Bilder,
		);
		result.push(...customFormStrucWithIndex);
	}

	for (let i = 0; i < bilderLengthMarkteintrittsbarrieren; i++) {
		const customFormStrucWithIndex = mapindexbyPath(
			customFormStrucMarkteintrittsbarrieren(application, i),
			i,
			P.Projektbeschreibung_Loewe.schema.attributes.Moegliche_Markteintrittsbarrieren_Bilder,
		);
		result.push(...customFormStrucWithIndex);
	}

	for (let i = 0; i < bilderLengthSchutzrechte; i++) {
		const customFormStrucWithIndex = mapindexbyPath(
			customFormStrucSchutzrechte(application, i),
			i,
			P.Projektbeschreibung_Loewe.schema.attributes.Schutzrechte_Lizenzen_Zwischen_Verbundpartnern_Bilder,
		);
		result.push(...customFormStrucWithIndex);
	}

	for (let i = 0; i < bilderLengthUnternehmensentwicklung; i++) {
		const customFormStrucWithIndex = mapindexbyPath(
			customFormStrucUnternehmensentwicklung(application, i),
			i,
			P.Projektbeschreibung_Loewe.schema.attributes.Unternehmensentwicklung_Erwartete_Arbeitsplatzeffekte_Bilder,
		);
		result.push(...customFormStrucWithIndex);
	}

	if (
		checkIfPruefAssitenzNeedsToValidate(
			application,
			P.Projektbeschreibung_Loewe.schema.attributes.Pruefung_Marktanalyse_Pruefstatus.path,
			P.Projektbeschreibung_Loewe.schema.attributes.Pruefung_Marktanalyse_Erledigt.path,
		)
	) {
		result.push(...formStrucPruefAssistenz());
	}
	// push with spread to avoid overwriting on same parent path
	result.push(...getFormStrucWithoutComponentTable(formStrucWettbewerbstechnologien()));
	result.push(...getFormStrucWithoutComponentTable(formStrucAnwendungsgebiete()));
	result.push(...getFormStrucWithoutComponentTable(formStrucMarkteintrittsstrategie()));
	result.push(...getFormStrucWithoutComponentTable(formStrucMarkteintrittsbarrieren()));
	result.push(...getFormStrucWithoutComponentTable(formStrucSchutzrechte()));
	result.push(...getFormStrucWithoutComponentTable(formStrucUnternehmensentwicklung()));

	return result;
};
